import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ChatBot = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Get current language
  const getCurrentLanguage = () => {
    const lang = i18n.language?.substring(0, 2) || 'en';
    return ['it', 'en', 'de', 'fr'].includes(lang) ? lang : 'en';
  };

  // Visibility logic - same as WhatsApp button
  useEffect(() => {
    const checkMobile = () => window.innerWidth < 640;
    const isMobile = checkMobile();

    if (isMobile) {
      // On mobile, show after 10 seconds
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10000);
      return () => clearTimeout(timer);
    } else {
      // On desktop, show after scrolling 300px
      const handleScroll = () => {
        setIsVisible(window.scrollY > 300);
      };
      window.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load welcome message when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadWelcomeMessage();
    }
  }, [isOpen]);

  const loadWelcomeMessage = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/welcome?language=${getCurrentLanguage()}`);
      const data = await response.json();
      setMessages([{ role: 'assistant', content: data.message }]);
    } catch (error) {
      console.error('Error loading welcome message:', error); // eslint-disable-line no-console
      // Fallback welcome message
      const fallbacks = {
        it: "👋 Ciao! Sono l'assistente di Double A Studio. Come posso aiutarti?",
        en: "👋 Hi! I'm the assistant of Double A Studio. How can I help you?",
        de: "👋 Hallo! Ich bin der Assistent von Double A Studio. Wie kann ich helfen?",
        fr: "👋 Bonjour! Je suis l'assistant de Double A Studio. Comment puis-je vous aider?"
      };
      setMessages([{ role: 'assistant', content: fallbacks[getCurrentLanguage()] }]);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          language: getCurrentLanguage()
        })
      });

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('Error sending message:', error); // eslint-disable-line no-console
      const errorMessages = {
        it: "Mi scuso, si è verificato un errore. Riprova o contattaci su WhatsApp.",
        en: "Sorry, an error occurred. Please try again or contact us on WhatsApp.",
        de: "Entschuldigung, ein Fehler ist aufgetreten. Bitte versuche es erneut oder kontaktiere uns auf WhatsApp.",
        fr: "Désolé, une erreur s'est produite. Réessayez ou contactez-nous sur WhatsApp."
      };
      setMessages(prev => [...prev, { role: 'assistant', content: errorMessages[getCurrentLanguage()] }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const labels = {
    it: { placeholder: "Scrivi un messaggio...", title: "Assistente", powered: "Powered by AI" },
    en: { placeholder: "Type a message...", title: "Assistant", powered: "Powered by AI" },
    de: { placeholder: "Nachricht schreiben...", title: "Assistent", powered: "Powered by AI" },
    fr: { placeholder: "Écrivez un message...", title: "Assistant", powered: "Powered by AI" }
  };

  const currentLabels = labels[getCurrentLanguage()] || labels.en;

  return (
    <>
      {/* Chat Toggle Button - positioned above WhatsApp */}
      <AnimatePresence>
        {!isOpen && isVisible && (
          <motion.button
            onClick={() => setIsOpen(true)}
            className="fixed bottom-20 right-2 sm:bottom-24 sm:right-6 flex items-center gap-2"
            style={{ zIndex: 9998 }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.3 }}
            data-testid="chatbot-toggle"
          >
            <motion.div
              className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#0a0a0a] border-2 border-[#FFD700] flex items-center justify-center shadow-lg shadow-[#FFD700]/20"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {/* Pulse animation */}
              <span className="absolute inset-0 rounded-full border-2 border-[#FFD700]/50 animate-ping" />
              
              {/* Chat icon */}
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-[#FFD700] relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </motion.div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed bottom-20 right-2 sm:bottom-24 sm:right-6 w-[calc(100vw-16px)] sm:w-96 h-[450px] sm:h-[500px] bg-[#0a0a0a] border border-[#FFD700]/30 rounded-2xl shadow-2xl shadow-black/50 flex flex-col overflow-hidden"
            style={{ zIndex: 9998, maxHeight: 'calc(100vh - 150px)' }}
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#FFD700]/20 bg-gradient-to-r from-[#0a0a0a] to-[#111]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFD700] to-[#B8860B] flex items-center justify-center">
                  <span className="text-black font-bold text-sm">DA</span>
                </div>
                <div>
                  <h3 className="text-white font-medium text-sm">{currentLabels.title}</h3>
                  <p className="text-[#FFD700]/60 text-xs">{currentLabels.powered}</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              >
                <svg className="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, index) => (
                <motion.div
                  key={`${msg.role}-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-[#FFD700] text-black rounded-br-md'
                        : 'bg-white/10 text-white/90 rounded-bl-md'
                    }`}
                  >
                    {msg.content}
                  </div>
                </motion.div>
              ))}
              
              {/* Loading indicator */}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white/10 px-4 py-3 rounded-2xl rounded-bl-md">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#FFD700]/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-[#FFD700]/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-[#FFD700]/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-[#FFD700]/20 bg-[#0a0a0a]">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={currentLabels.placeholder}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#FFD700]/50 transition-colors"
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !inputValue.trim()}
                  className="w-12 h-12 bg-[#FFD700] hover:bg-[#FFD700]/90 disabled:bg-[#FFD700]/30 rounded-xl flex items-center justify-center transition-colors"
                >
                  <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;
